/**
 * PropRowView — the schema-engine row renderer (Faz 5.5). One component per
 * PropValue kind, all sharing the compact label-left / control-right grid of
 * the properties palette. Labels come through i18n; the variances marker
 (*VARIES*) replaces control values when the selection disagrees.
 */

import {
    Box,
    Button,
    Flex,
    Grid,
    Input,
    NumberInput,
    NumberInputField,
    Select,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AciColorPicker } from '../../../components/cad/AciColorPicker';
import { useAppStore, type Layer } from '../../../state/store';
import { VARIES, type PropRow } from './schema';

/** Classic 27-value lineweight table (mm) — matches the ribbon combo. */
const LINEWEIGHTS = [0, 0.05, 0.09, 0.13, 0.15, 0.18, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5, 0.53, 0.6, 0.7, 0.8, 0.9, 1.0, 1.06, 1.2, 1.4, 1.58, 2.0, 2.11, 2.5, 3.0, 3.5];

const LINETYPES = ['BYLAYER', 'BYBLOCK', 'CONTINUOUS', 'DASHED', 'HIDDEN', 'CENTER', 'PHANTOM', 'DASHDOT', 'DOTTED'] as const;

export function rowLabel(row: PropRow, t: TFunction<'translation', undefined>): string {
    return row.labelOverride ?? t(row.labelKey as never);
}

export function PropRowView({ row }: { row: PropRow }) {
    const { t } = useTranslation();
    const v = row.value;

    return (
        <Grid templateColumns="96px 1fr" gap={2} alignItems="center" minW={0}>
            <Text fontSize="xs" color="text.secondary" noOfLines={1} title={rowLabel(row, t)}>
                {rowLabel(row, t)}
            </Text>
            <PropControl row={row} />
        </Grid>
    );
}

function PropControl({ row }: { row: PropRow }) {
    const { t } = useTranslation();
    const v = row.value;

    switch (v.kind) {
        case 'editText':
            return (
                <Input
                    size="sm"
                    variant="filled"
                    bg="surface.300"
                    color="text.primary"
                    fontSize="xs"
                    defaultValue={v.value}
                    key={`${row.id}:${v.value}`}
                    onBlur={(e) => {
                        if (e.target.value !== v.value) v.commit(e.target.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.currentTarget.value !== v.value) {
                            v.commit(e.currentTarget.value);
                        }
                    }}
                    minW={0}
                />
            );

        case 'readOnly':
            return (
                <Tooltip label={v.tooltip ? t(v.tooltip as never) : undefined} isDisabled={!v.tooltip} hasArrow>
                    <Text fontSize="xs" color="text.primary" noOfLines={1} minW={0}>
                        {v.text}
                    </Text>
                </Tooltip>
            );

        case 'coordinate':
            return (
                <Flex gap={1} minW={0}>
                    {v.axes.map((ax) => (
                        <NumberInput
                            key={`${ax.axis}:${ax.value}`}
                            size="sm"
                            variant="filled"
                            bg="surface.300"
                            color="text.primary"
                            flex="1"
                            minW={0}
                            defaultValue={ax.value ?? undefined}
                            onBlur={(e) => {
                                const n = Number((e.target as HTMLInputElement).value);
                                if (Number.isFinite(n)) ax.commit(n);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    const n = Number((e.target as HTMLInputElement).value);
                                    if (Number.isFinite(n)) ax.commit(n);
                                }
                            }}
                        >
                            <NumberInputField placeholder={ax.axis.toUpperCase()} fontSize="xs" />
                        </NumberInput>
                    ))}
                </Flex>
            );

        case 'layerChoice':
            return (
                <LayerSelect value={v.value} varies={v.varies} onCommit={v.commit} />
            );

        case 'colorChoice':
            return (
                <AciColorPicker
                    value={v.value}
                    varies={v.varies}
                    onCommit={(color) => v.commit(color)}
                />
            );

        case 'linetypeChoice':
            return (
                <Select
                    size="sm"
                    variant="filled"
                    bg="surface.300"
                    color="text.primary"
                    fontSize="xs"
                    value={v.value}
                    onChange={(e) => v.commit(e.target.value)}
                    minW={0}
                >
                    {v.varies && <option value="">{VARIES}</option>}
                    {LINETYPES.map((lt) => (
                        <option key={lt} value={lt}>{lt}</option>
                    ))}
                </Select>
            );

        case 'lwChoice':
            return (
                <Select
                    size="sm"
                    variant="filled"
                    bg="surface.300"
                    color="text.primary"
                    fontSize="xs"
                    value={String(v.value)}
                    onChange={(e) => {
                        const raw = e.target.value;
                        v.commit(raw === 'default' ? 'default' : Number(raw));
                    }}
                    minW={0}
                >
                    {v.varies && <option value="">{VARIES}</option>}
                    <option value="default">{t('ui.cad.props.byLayer')}</option>
                    {LINEWEIGHTS.map((lw) => (
                        <option key={lw} value={lw}>{lw.toFixed(2)} mm</option>
                    ))}
                </Select>
            );

        case 'transparencyEdit':
            return (
                <NumberInput
                    size="sm"
                    variant="filled"
                    bg="surface.300"
                    color="text.primary"
                    min={0}
                    max={100}
                    defaultValue={v.value ?? ''}
                    key={`${row.id}:${v.value ?? 'varies'}`}
                    onBlur={(e) => {
                        const n = Number((e.target as HTMLInputElement).value);
                        if ((e.target as HTMLInputElement).value !== '' && Number.isFinite(n)) v.commit(n);
                        else if ((e.target as HTMLInputElement).value === '') v.commit(null);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            const n = Number((e.currentTarget as HTMLInputElement).value);
                            if ((e.currentTarget as HTMLInputElement).value !== '' && Number.isFinite(n)) v.commit(n);
                        }
                    }}
                    minW={0}
                >
                    <NumberInputField placeholder={t('ui.cad.props.byLayer')} fontSize="xs" />
                </NumberInput>
            );

        case 'boolToggle':
            return (
                <Select
                    size="sm"
                    variant="filled"
                    bg="surface.300"
                    color="text.primary"
                    fontSize="xs"
                    value={v.value ? 'yes' : 'no'}
                    onChange={(e) => v.commit(e.target.value === 'yes')}
                    minW={0}
                >
                    {v.varies && <option value="">{VARIES}</option>}
                    <option value="yes">{t('ui.cad.props.yes')}</option>
                    <option value="no">{t('ui.cad.props.no')}</option>
                </Select>
            );

        case 'numberInput':
            return (
                <NumberInput
                    size="sm"
                    variant="filled"
                    bg="surface.300"
                    color="text.primary"
                    min={v.min}
                    defaultValue={v.value}
                    key={`${row.id}:${v.value}`}
                    onBlur={(e) => {
                        const n = Number((e.target as HTMLInputElement).value);
                        if (Number.isFinite(n) && (!v.min || n >= v.min)) v.commit(n);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            const n = Number((e.currentTarget as HTMLInputElement).value);
                            if (Number.isFinite(n) && (!v.min || n >= v.min)) v.commit(n);
                        }
                    }}
                    minW={0}
                >
                    <NumberInputField
                        placeholder={v.varies ? VARIES : undefined}
                        fontSize="xs"
                    />
                </NumberInput>
            );

        case 'stepper':
            return (
                <Flex align="center" gap={1} minW={0}>
                    <Button
                        size="xs"
                        variant="ghost"
                        aria-label={t('ui.cad.props.prevVertex')}
                        isDisabled={v.index <= 0}
                        onClick={() => v.onIndex(v.index - 1)}
                        flexShrink={0}
                        px={1}
                    >
                        <ChevronLeft size={13} />
                    </Button>
                    <Text fontSize="xs" color="text.primary" flexShrink={0}>
                        {v.index + 1} / {v.count}
                    </Text>
                    <Button
                        size="xs"
                        variant="ghost"
                        aria-label={t('ui.cad.props.nextVertex')}
                        isDisabled={v.index >= v.count - 1}
                        onClick={() => v.onIndex(v.index + 1)}
                        flexShrink={0}
                        px={1}
                    >
                        <ChevronRight size={13} />
                    </Button>
                </Flex>
            );

        case 'hyperlink':
            return (
                <Box
                    as="a"
                    href={v.url}
                    fontSize="xs"
                    color="brand.500"
                    noOfLines={1}
                    onClick={(e) => e.stopPropagation()}
                >
                    {v.text}
                </Box>
            );

        default:
            return null;
    }
}

function LayerSelect({ value, varies, onCommit }: {
    value: string | null;
    varies: boolean;
    onCommit: (layerId: string) => void;
}) {
    const { t } = useTranslation();
    const layers = useAppStore((s) => Object.values(s.layers));
    return (
        <Select
            size="sm"
            variant="filled"
            bg="surface.300"
            color="text.primary"
            fontSize="xs"
            value={value ?? ''}
            onChange={(e) => onCommit(e.target.value)}
            minW={0}
        >
            {varies && <option value="">{VARIES}</option>}
            {layers.map((l: Layer) => (
                <option key={l.id} value={l.id}>{l.name}</option>
            ))}
            {layers.length === 0 && <option value="">{t('ui.cad.props.noLayers')}</option>}
        </Select>
    );
}
